import { Component, OnInit } from '@angular/core';
import { CaseDetails, WeekDays } from '../patient-checklist/services/patient-checklist.service';
import { GetItemsParams } from '../attorney/service/attorney.model';
import { debounceTime, distinctUntilChanged, Subject, switchMap, takeUntil } from 'rxjs';
import { IWeekDetails } from 'src/app/components/week-picker/week-picker.component';
import { SharedService } from 'src/app/services/shared.service';
import { ToastService } from 'src/app/services/toast.service';
import { DialogueService } from 'src/app/services/dialogue.service';
import { CaseType, Page, ToastType } from 'src/app/enums/enums';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { TransportationChecklistService } from './services/transportation-checklist.service';

@Component({
  selector: 'app-transportation-checklist',
  templateUrl: './transportation-checklist.component.html',
  styleUrls: ['./transportation-checklist.component.scss']
})
export class TransportationChecklistComponent implements OnInit {
  public IsLoading = false;
  public TransportationCheckList!: CaseDetails[];
  public TotalTransportations!: number;
  public PaginationParams!: GetItemsParams;
  private Unsubscribe = new Subject<void>();
  public TodayDate = new Date();
  public Weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  public SelectedWeekDetails!: IWeekDetails;
  public SearchVal: string = '';
  private SearchSubject = new Subject<string>();

  constructor(private transportationCheckListService: TransportationChecklistService, public sharedService: SharedService, public toastService: ToastService, public dialogue: DialogueService) {
    this.PaginationParams = {
      searchValue: '',
      searchDate: '2024-11-14',
      clinicId: this.sharedService.CurrentClinic.value || 0,
      pageNumber: 1,
      pageSize: Page.Size,
      sortOrder: 'asc',
      sortColumn: 'mriFacilityName',
      skip: 0,
      take: Number(this.sharedService.getData('records')) || Page.Size
    };
  }

  ngOnInit(): void {
    this.SearchSubject.pipe(
      debounceTime(500), // Wait for 300ms pause in input
      distinctUntilChanged(), // Ignore if the search term is the same as previous
      switchMap((term) => {
        this.PaginationParams.searchValue = term;
        this.GetTransportationCheckList(this.PaginationParams);
        return '';
      })
    ).subscribe((results) => {
      console.log('results', results);
    });
  }

  SearchData() {
    this.SearchSubject.next(this.SearchVal);
  }

  GetSelectedWeek(val: { start: NgbDate; end: NgbDate }) {
    const StartDayOfWeek = `${val.start.year}-${val.start.month}-${val.start.day}`;
    this.PaginationParams.searchDate = StartDayOfWeek;

    this.sharedService.CurrentClinic.pipe(takeUntil(this.Unsubscribe)).subscribe((val) => {
      this.PaginationParams.clinicId = Number(val);
      this.GetTransportationCheckList(this.PaginationParams);
    });
  }

  GetWeekDetails(val: IWeekDetails) {
    this.SelectedWeekDetails = { ...val };
  }

  GetTransportationCheckList(params: GetItemsParams) {
    if (params.clinicId !== 0) {
      this.IsLoading = true;
      this.transportationCheckListService.GetTransportationCheckList(params).subscribe({
        next: (res) => {
          this.TransportationCheckList = [...res.result.transportationCallingResponse];
          this.TotalTransportations = res.result.total;
          this.TransportationCheckList.map((transportation) => {
            transportation.caseName = CaseType[transportation.caseType] || '';
          });
        },
        error: (err) => {
          this.IsLoading = false;
        },
        complete: () => (this.IsLoading = false)
      });
    } else {
      this.toastService.showToast({
        message: 'Please, Select clinic from Dropdown in Header',
        type: ToastType.Info
      });
    }
  }

  GetMarkConfirmation(event: Event, item: CaseDetails, weekday: string) {
    event.preventDefault();
    const checkboxEle = event.target as HTMLInputElement;
    const confirmMeta: {
      alertTitle?: string;
      alertMessage?: string;
      alertAction?: {
        actionName: string;
        actionClass: string;
      };
    } = {};
    if (checkboxEle.checked) {
      confirmMeta.alertTitle = 'Marked as Visited?';
      confirmMeta.alertMessage = `Are you sure you want <b class="text-black">${item.lastName} ${item.firstName}</b> is Marked as Visited?`;
      confirmMeta.alertAction = { actionName: 'Visited', actionClass: '' };
    } else {
      confirmMeta.alertTitle = 'Are you sure?';
      confirmMeta.alertMessage = `Are you sure you want to mark <b class="text-black">${item.lastName} ${item.firstName}</b> as Not Visited?`;
      confirmMeta.alertAction = {
        actionName: 'Yes, Remove',
        actionClass: 'btn-danger'
      };
    }

    const getConfirmation = this.dialogue.GetConfirmation(confirmMeta.alertTitle, confirmMeta.alertMessage, confirmMeta.alertAction);
    getConfirmation.result.then((res) => {
      if (res) {
        if (event.target) checkboxEle.checked = !checkboxEle.checked;
        this.UpdateVisited(item, checkboxEle.checked, weekday);
      }
    });
  }

  private UpdateVisited(info: CaseDetails, value: boolean, weekday: string) {
    const reqBody: WeekDays = {
      patientId: info.patientId,
      monday: info.monday,
      tuesday: info.tuesday,
      wednesday: info.wednesday,
      thursday: info.thursday,
      friday: info.friday,
      saturday: info.saturday
    };

    reqBody[weekday] = value;
    // this.IsLoading = true;
    const response = this.transportationCheckListService.UpdateTransportationAttendance(info.id, reqBody);
    response.subscribe({
      next: (res) => {
        if (res.result === 1) {
          this.toastService.showToast({
            message: `Patient ${info.lastName} ${info.firstName} is marked as ${value ? 'visited' : 'Not visited'} on ${weekday}`,
            type: ToastType.Success
          });
        }
      },
      error: (err) => {
        console.log(err);
      },
      complete: () => {}
    });
  }

  /* Pagination */
  onPageChange(newPage: number): void {
    this.PaginationParams.pageNumber = newPage;
    this.GetTransportationCheckList(this.PaginationParams);

    /* Just to maintain Pagination and Record Index */
    this.PaginationParams.skip = (newPage - 1) * this.PaginationParams.take;
  }

  onPageSizeChange(newPageSize: number): void {
    this.PaginationParams.pageSize = newPageSize;
    this.sharedService.setData('records', this.PaginationParams.take);
    this.GetTransportationCheckList(this.PaginationParams);

    /* Just to maintain Pagination and Record Index */
    this.PaginationParams.take = newPageSize;
    this.PaginationParams.skip = 0;
  }
}
